/**
 * PLAN-SHOW-01 (Seth 2026-09-24: "Financing should be shown which specific
 * financing plan was selected so office knows which plan to process").
 *
 * What the customer chose when they accepted the proposal: pay in full, or
 * financing on ONE plan — the plan's name and the lender's plan number the
 * office keys in when it processes the loan. quote_accept saves the plan in
 * acceptance_json.financing_plan_id; an older or hand-marked acceptance falls
 * back to the plan the salesperson put on the accepted option.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { CopyCode } from "@/components/Copyable";

export interface SalePlan {
  id: string;
  name: string;
  plan_number: string | null;
  apr: number | null;
  term_months: number | null;
}

/** The accepted-proposal fields this needs (pegasus_quote). */
export interface QuotePayFields {
  id: string;
  accepted_option_id: string | null;
  payment_preference?: string | null;
  acceptance_json?: { financing_plan_id?: string | null } | null;
}

export type SaleChoice = "full" | "monthly" | "klarna" | null;

export function saleChoice(q: QuotePayFields | null | undefined): SaleChoice {
  const p = (q?.payment_preference ?? "").toLowerCase();
  // PAY-05a: "klarna" = monthly through Klarna at checkout (no financing plan of ours).
  return p === "monthly" ? "monthly" : p === "klarna" ? "klarna" : p === "full" ? "full" : null;
}

/** The financing plan the customer picked (null when they paid in full / nothing recorded). */
export function useSalePlan(q: QuotePayFields | null | undefined) {
  const choice = saleChoice(q);
  const fromAccept = q?.acceptance_json?.financing_plan_id ?? null;
  return useQuery({
    queryKey: ["sale_plan", q?.id ?? null, choice, fromAccept, q?.accepted_option_id ?? null],
    enabled: !!q?.id && choice === "monthly",
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SalePlan | null> => {
      let planId = fromAccept;
      if (!planId && q?.accepted_option_id) {
        const { data } = await supabase.from("pegasus_quote_option").select("financing_plan_id").eq("id", q.accepted_option_id).maybeSingle();
        planId = (data as { financing_plan_id: string | null } | null)?.financing_plan_id ?? null;
      }
      if (!planId) return null;
      const { data: p } = await supabase.from("pegasus_financing_plan")
        .select("id, name, plan_number, apr, term_months").eq("id", planId).maybeSingle();
      return (p as SalePlan | null) ?? null;
    },
  });
}

/** One line: "Paying in full" or "Financing: 18 Months No Interest · Plan 1066". */
export function SalePaymentLine({ quote, className }: { quote: QuotePayFields | null | undefined; className?: string }) {
  const choice = saleChoice(quote);
  const { data: plan, isLoading } = useSalePlan(quote);
  if (!choice) return null;
  if (choice === "full") {
    return <span className={className}>Paying <strong className="text-slate-800">in full</strong></span>;
  }
  if (choice === "klarna") {
    return <span className={className}>Paying <strong className="text-slate-800">monthly with Klarna</strong> · at checkout</span>;
  }
  if (isLoading) return <span className={className}>Financing…</span>;
  if (!plan) {
    return <span className={className}>Financing — <strong className="text-amber-700">no plan picked</strong></span>;
  }
  return (
    <span className={className}>
      Financing: <strong className="text-slate-800">{plan.name}</strong>
      {plan.plan_number && (
        <> · Plan <CopyCode value={plan.plan_number} label="Copy plan number" variant="text" size="md" className="font-bold text-slate-800" /></>
      )}
    </span>
  );
}
